import { SOFTWARE_TERMS, TIERS } from './tiers';
import { PRICING_CATEGORIES, type PricingCategoryId } from './pricing-nav';
import { servicePackages } from './services';

/**
 * The referral partner programme, said once. The /partners page, its
 * calculator and its form all read from here, so a change to the terms is one
 * edit. Applications are reviewed by hand: nobody becomes a commission partner
 * just by filling in the form.
 *
 * Commission applies to all three ways of working with KIT: Website & Software,
 * Managed Marketing and the Growth Partnership. The free days are on Website &
 * Software plans only.
 */
export const PARTNER_TERMS = {
  /** Share of each successful paid invoice the partner earns. */
  commission: 0.15,
  /** How many of the referred customer's successful paid invoices earn commission. */
  paidInvoices: 3,
  /** Discount the referred business gets on its first paid months. */
  referredDiscount: 0.1,
  /** How many paid months that discount covers. */
  discountedMonths: 3,
  freeDays: SOFTWARE_TERMS.freeDays,
} as const;

export const PARTNER_ROLES = [
  'Marketing freelancer',
  'Digital marketing agency',
  'Web designer or developer',
  'Business consultant or coach',
  'Existing KIT customer',
  'Other',
] as const;

export const PARTNER_AUDIENCES = ['HVAC businesses', 'Home service businesses', 'Multiple industries', 'Other'] as const;

/**
 * What a partner earns from one referred customer on a plan priced `monthly`:
 * commission on each of the first paid invoices, which are the discounted ones.
 */
export function commissionFor(monthly: number): number {
  const t = PARTNER_TERMS;
  const discountedInvoices = Math.min(t.paidInvoices, t.discountedMonths);
  const fullInvoices = t.paidInvoices - discountedInvoices;
  const billed = discountedInvoices * monthly * (1 - t.referredDiscount) + fullInvoices * monthly;
  return Math.round(billed * t.commission);
}

export const pct = (n: number) => `${Math.round(n * 100)}%`;

export type ReferralPlan = { id: string; name: string; price: number; priceLabel: string; priceIsFrom?: boolean };
export type ReferralService = { id: PricingCategoryId; label: string; from: string; freeDays: boolean; plans: ReferralPlan[] };

/** Every plan and package a referred business can start on, grouped the way the pricing pages are. */
export const REFERRAL_SERVICES: ReferralService[] = PRICING_CATEGORIES.map((c) => ({
  id: c.id,
  label: c.label,
  from: c.from,
  freeDays: c.id === 'software',
  plans:
    c.id === 'software'
      ? TIERS.flatMap((t) => (t.price ? [{ id: t.id, name: t.name, price: t.price, priceLabel: t.priceLabel }] : []))
      : servicePackages(c.id).map((p) => ({ id: p.id, name: p.name, price: p.price, priceLabel: p.priceLabel, priceIsFrom: p.priceIsFrom })),
}));
